import React, { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { cn } from '@/lib/utils';
import { toNumber } from './money';

// Strip everything except digits, at most one '.' (unless `integer`), and a
// leading '-' (only when `allowNegative`). '7.5' with `integer` → '75'.
function sanitize(raw, integer, allowNegative) {
  const s = String(raw ?? '');
  let sign = '';
  let rest = s;
  if (allowNegative && rest.startsWith('-')) {
    sign = '-';
    rest = rest.slice(1);
  }
  rest = rest.replace(/-/g, '');
  if (integer) {
    rest = rest.replace(/\./g, '');
  } else {
    const firstDot = rest.indexOf('.');
    if (firstDot !== -1) {
      rest = rest.slice(0, firstDot + 1) + rest.slice(firstDot + 1).replace(/\./g, '');
    }
  }
  rest = rest.replace(/[^0-9.]/g, '');
  return sign + rest;
}

// '', '.', '-', '-.' → null; else Number(text) (finite) — '6.' → 6, '.5' → 0.5.
function parse(text) {
  if (text === '' || text === '.' || text === '-' || text === '-.') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

// Plain numeric input (ticket 125): credit scores, beds, months, percentages, rates.
// type="text" + inputMode so mobile shows a numeric keypad without the
// <input type="number"> quirks (scroll-wheel edits, 'e', leading zeros).
// Stores a number or null. No grouping, no '$' — use MoneyInput for dollars.
export default function NumberInput({ value, onChange, integer = false, allowNegative = false, min, max, suffix, className, ...rest }) {
  const [text, setText] = useState(() => (value == null || value === '' ? '' : String(value)));

  useEffect(() => {
    if (parse(text) !== toNumber(value)) {
      setText(value == null || value === '' ? '' : String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handle = (e) => {
    const cleaned = sanitize(e.target.value, integer, allowNegative);
    setText(cleaned);
    onChange(parse(cleaned));
  };

  const blur = (e) => {
    let v = parse(text);
    if (v !== null) {
      let clamped = v;
      if (min != null) clamped = Math.max(clamped, min);
      if (max != null) clamped = Math.min(clamped, max);
      if (clamped !== v) onChange(clamped);
      v = clamped;
    }
    setText(v === null ? '' : String(v));
    rest.onBlur?.(e);
  };

  const input = (
    <Input
      autoComplete="off"
      {...rest}
      type="text"
      inputMode={integer && !allowNegative ? 'numeric' : 'decimal'}
      className={cn('tabular-nums', className)}
      value={text}
      onChange={handle}
      onBlur={blur}
    />
  );

  if (suffix) {
    return (
      <div className="flex items-center gap-1">
        {input}
        <span className="text-xs text-slate-500">{suffix}</span>
      </div>
    );
  }
  return input;
}
